import { useState, useCallback, useEffect } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { Select, Input, Button, Table, Pagination, Alert, Badge, DatePicker, Modal, type TableColumn } from '@/ui'
import { formatDate } from '@/ui'
import styles from './AuditLogs.module.css'

interface AuditLog {
  id: string
  action: string
  category: string
  userId: string | null
  userEmail: string | null
  userName: string | null
  targetId: string | null
  targetType: string | null
  metadata: Record<string, unknown> | null
  ipAddress: string | null
  status: string
  createdAt: string
}

interface AuditLogsResponse {
  logs: AuditLog[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

const CATEGORY_OPTIONS = [
  { value: '', label: '전체 카테고리' },
  { value: 'AUTH', label: 'AUTH' },
  { value: 'PAYMENT', label: 'PAYMENT' },
  { value: 'ADMIN', label: 'ADMIN' },
  { value: 'USER', label: 'USER' },
  { value: 'INVITATION', label: 'INVITATION' },
]

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'SUCCESS', label: 'SUCCESS' },
  { value: 'FAILURE', label: 'FAILURE' },
]

const PERIOD_OPTIONS = [
  { value: '', label: '기간 선택' },
  { value: '7', label: '최근 1주일' },
  { value: '30', label: '최근 1개월' },
  { value: '90', label: '최근 3개월' },
]

const CATEGORY_COLOR: Record<string, 'blue' | 'green' | 'yellow' | 'red' | 'gray'> = {
  AUTH: 'blue',
  PAYMENT: 'green',
  ADMIN: 'yellow',
  USER: 'gray',
  INVITATION: 'gray',
}

// 상세 컬럼은 키 목록만 짧게 (전체 값은 행 클릭 시 모달에서 표시)
function summarizeMetadata(metadata: Record<string, unknown> | null): string {
  if (!metadata || Object.keys(metadata).length === 0) return '-'
  return Object.keys(metadata).join(', ')
}

export function AuditLogs() {
  const { filters, setFilters, resetFilters } = useAdminFilters()
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [appliedUserSearch, setAppliedUserSearch] = useState('')
  const [periodFilter, setPeriodFilter] = useState('7')
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return d.toISOString().slice(0, 10)
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10))

  // 행 클릭 시 상세 모달에 표시할 로그
  const [selected, setSelected] = useState<AuditLog | null>(null)

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortOrder', filters.sortOrder)
  if (categoryFilter) params.set('category', categoryFilter)
  if (statusFilter) params.set('status', statusFilter)
  if (appliedUserSearch) params.set('userId', appliedUserSearch)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)

  const { data, isLoading, error, refetch } = useApiFetch<AuditLogsResponse>(
    `/api/admin/audit-logs?${params.toString()}`
  )

  const handleSearch = useCallback(() => {
    setAppliedUserSearch(userSearch.trim())
    setFilters({ page: 1 })
  }, [userSearch, setFilters])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }, [handleSearch])

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  const columns: TableColumn<AuditLog>[] = [
    {
      key: 'createdAt',
      label: '시간',
      render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm:ss'),
      width: '150px',
    },
    {
      key: 'category',
      label: '카테고리',
      render: (row) => <Badge color={CATEGORY_COLOR[row.category] ?? 'gray'}>{row.category}</Badge>,
      width: '100px',
    },
    {
      key: 'action',
      label: '액션',
      width: '180px',
    },
    {
      key: 'user',
      label: '사용자',
      render: (row) => row.userEmail ?? row.userId?.slice(0, 8) ?? '-',
    },
    {
      key: 'target',
      label: '대상',
      render: (row) => row.targetType ? `${row.targetType}:${row.targetId?.slice(0, 8) ?? ''}` : '-',
      width: '120px',
    },
    {
      key: 'status',
      label: '상태',
      render: (row) => (
        <Badge color={row.status === 'SUCCESS' ? 'green' : 'red'}>
          {row.status}
        </Badge>
      ),
      width: '90px',
    },
    {
      key: 'metadata',
      label: '상세',
      render: (row) => (
        <span className={styles.metadataCell} title="클릭하여 전체 보기">
          {summarizeMetadata(row.metadata)}
        </span>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <Input
          size="sm"
          placeholder="유저 ID 또는 이메일"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button variant="secondary" size="sm" onClick={handleSearch}>
          검색
        </Button>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoading} aria-label="새로고침">
          &#x21bb;
        </Button>
        <Select
          size="sm"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setFilters({ page: 1 }) }}
          options={CATEGORY_OPTIONS}
        />
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }}
          options={STATUS_OPTIONS}
        />
        <Select
          size="sm"
          value={periodFilter}
          onChange={(e) => {
            const val = e.target.value
            setPeriodFilter(val)
            const days = Number(val)
            if (days) {
              const d = new Date()
              d.setDate(d.getDate() - days)
              setStartDate(d.toISOString().slice(0, 10))
              setEndDate(new Date().toISOString().slice(0, 10))
            } else {
              setStartDate('')
              setEndDate('')
            }
            setFilters({ page: 1 })
          }}
          options={PERIOD_OPTIONS}
        />
        <DatePicker
          size="sm"
          value={startDate}
          onChange={(_date, formatted) => { setStartDate(formatted); setPeriodFilter(''); setFilters({ page: 1 }) }}
        />
        <DatePicker
          size="sm"
          value={endDate}
          onChange={(_date, formatted) => { setEndDate(formatted); setPeriodFilter(''); setFilters({ page: 1 }) }}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AuditLog>
        columns={columns}
        data={data?.logs ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={() => {}}
        onRowClick={setSelected}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}

      {/* 상세 모달 */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title="감사 로그 상세"
      >
        {selected && (
          <div className={styles.detail}>
            <dl className={styles.detailGrid}>
              <dt>시간</dt>
              <dd>{formatDate(selected.createdAt, 'yyyy.M.d HH:mm:ss')}</dd>

              <dt>카테고리</dt>
              <dd><Badge color={CATEGORY_COLOR[selected.category] ?? 'gray'}>{selected.category}</Badge></dd>

              <dt>액션</dt>
              <dd><code className={styles.code}>{selected.action}</code></dd>

              <dt>상태</dt>
              <dd>
                <Badge color={selected.status === 'SUCCESS' ? 'green' : 'red'}>{selected.status}</Badge>
              </dd>

              <dt>사용자</dt>
              <dd>
                {selected.userName && <span>{selected.userName} </span>}
                {selected.userEmail && <span className={styles.dim}>{selected.userEmail}</span>}
                {selected.userId && <div className={styles.dim}><code className={styles.code}>{selected.userId}</code></div>}
                {!selected.userName && !selected.userEmail && !selected.userId && '-'}
              </dd>

              <dt>대상</dt>
              <dd>
                {selected.targetType
                  ? <code className={styles.code}>{selected.targetType}:{selected.targetId ?? ''}</code>
                  : '-'}
              </dd>

              <dt>IP</dt>
              <dd>{selected.ipAddress ?? '-'}</dd>
            </dl>

            <div className={styles.metaSection}>
              <div className={styles.metaLabel}>metadata</div>
              <pre className={styles.metaJson}>
                {selected.metadata && Object.keys(selected.metadata).length > 0
                  ? JSON.stringify(selected.metadata, null, 2)
                  : '(없음)'}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
